import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { PageHeader, Panel, Field, ErrorText, StatusBadge, btnPrimary, inputCls } from "@/components/portal/ui";

const STATUS_TEXT = {
  pending: "La tua candidatura è in valutazione. Puoi già completare il profilo e, se sei medico, impostare i tuoi orari: saranno pubblicati appena il team TytoCare approverà l'account.",
  approved: "Il tuo profilo è attivo.",
};

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/staff/me").then((r) => {
      setProfile(r.data);
      setForm({ first_name: r.data.first_name, last_name: r.data.last_name, phone: r.data.phone || "", city: r.data.city || "", license_number: r.data.license_number || "", bio: r.data.bio || "" });
    });
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put("/staff/me", form);
      setProfile(data);
      setUser({ ...user, ...data });
      toast.success("Profilo aggiornato");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;
  const isDoctor = user.role === "doctor";

  return (
    <div data-testid="profile-page">
      <PageHeader eyebrow="Profilo" title={`${profile.first_name} ${profile.last_name}`} desc={profile.email} />

      <div className={`mb-6 rounded-3xl p-5 ring-1 ${profile.status === "approved" ? "bg-emerald-50 ring-emerald-200" : "bg-amber-50 ring-amber-200"}`} data-testid="profile-status-banner">
        <div className="flex items-center gap-3">
          <StatusBadge status={profile.status} testid="profile-status-badge" />
          <p className="text-sm text-[#4A4059]">{STATUS_TEXT[profile.status] || "Contatta l'amministrazione per informazioni sul tuo account."}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel className="lg:col-span-2">
          <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="profile-form">
            <Field label="Nome" htmlFor="pf-first"><input id="pf-first" required value={form.first_name} onChange={set("first_name")} className={inputCls} data-testid="profile-first-name-input" /></Field>
            <Field label="Cognome" htmlFor="pf-last"><input id="pf-last" required value={form.last_name} onChange={set("last_name")} className={inputCls} data-testid="profile-last-name-input" /></Field>
            <Field label="Telefono" htmlFor="pf-phone"><input id="pf-phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="profile-phone-input" /></Field>
            <Field label="Città / zona" htmlFor="pf-city"><input id="pf-city" value={form.city} onChange={set("city")} className={inputCls} data-testid="profile-city-input" /></Field>
            <Field label="N. iscrizione" htmlFor="pf-license" className="sm:col-span-2"><input id="pf-license" value={form.license_number} onChange={set("license_number")} className={inputCls} data-testid="profile-license-input" /></Field>
            {isDoctor && (
              <Field label="Presentazione" htmlFor="pf-bio" className="sm:col-span-2"><textarea id="pf-bio" rows={3} value={form.bio} onChange={set("bio")} className={inputCls} data-testid="profile-bio-input" /></Field>
            )}
            <div className="sm:col-span-2"><ErrorText testid="profile-error">{error}</ErrorText></div>
            <div className="sm:col-span-2 flex justify-end">
              <button type="submit" disabled={saving} className={btnPrimary} data-testid="profile-save-btn">{saving ? "Salvataggio…" : "Salva profilo"}</button>
            </div>
          </form>
        </Panel>

        {isDoctor && (
          <Panel>
            <h3 className="font-heading text-lg font-semibold">Le tue specialità</h3>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="profile-specialties">
              {(profile.specialties || []).map((s) => (
                <span key={s.id} className="rounded-full bg-[#5B2C91]/10 px-3 py-1 text-xs font-semibold text-[#5B2C91]">{s.name}</span>
              ))}
              {(profile.specialties || []).length === 0 && <span className="text-sm text-[#8A809A]">Nessuna specialità assegnata.</span>}
            </div>
            <p className="mt-4 text-xs text-[#8A809A]">Per modificare le specialità contatta l'amministrazione TytoCare.</p>
          </Panel>
        )}
      </div>
    </div>
  );
}
